import { formatScore, formatScoreDelta, formatUtcDate, formatCount } from './format';

// A company's score across the occasions of one read, and the geometry for drawing it.
//
// Nothing here is fetched. A row is one (company, knowable-at) — the endpoint's grain, stated at
// aggregateRows.js — so the series is already in the response, spread down the rows instead of
// gathered. Two other modules gather it for their own purposes and throw the grouping away:
// attachPreviousScores (services/feedView.js) walks each company's occasions to find each row's
// predecessor, and aggregateByCompany groups the same rows to average them. This one keeps it.
//
// THE RULE THAT DECIDES EVERYTHING ELSE: THE SERIES IS BUILT FROM THE WHOLE READ, NEVER FROM A
// FILTERED VIEW. Three things break otherwise, and each of them is a lie on screen rather than a
// rough edge:
//
//   1. The missing-scores toggle keeps the rows with NO score (rowFilters.js). A series built over
//      that view is a series of nulls, so every line would vanish at the exact moment the analyst
//      asked to look at the gaps.
//   2. The knowable-window filter removes occasions from the MIDDLE of a series. A break drawn there
//      is indistinguishable from the break that means "this occasion scored nothing" — the one
//      conflation this page exists to refuse, and the reason unscored rows are kept with a null
//      score rather than dropped (services/feedView.js).
//   3. It would disagree with the two cells beside it. attachPreviousScores runs inside the
//      normalizer, so "previous" means the previous occasion in the READ and no client-side
//      narrowing can redefine it. A line that narrowed while Prev score did not would put two
//      different meanings of "before" in adjacent columns of one row.
//
// The cost is real and must be stated on screen rather than hidden: while the table is averaging,
// the Score column covers the FILTERED occasions and the line beside it covers the read. Both spans
// are named in the cell's tooltip; neither is left to be inferred.

// The cell's drawing box. Here rather than in the component because the same geometry is used at
// chart size by the history panel, and two boxes declared in two files is how a gap ends up drawn
// one way in the table and another way in the panel.
export const SPARK_BOX = { width: 112, height: 26, padding: 3 };

// Two scored occasions are the floor for a shape. One draws a dot of unknown meaning in a 26px box,
// so the cell says what it has instead — the same instinct canAggregate applies to its toggle.
export const MIN_PLOTTABLE = 2;

const asFiniteNumber = (value) => (typeof value === 'number' && isFinite(value) ? value : null);

// `new Date(null).getTime()` is 0, not NaN, so a falsy stamp has to be rejected before the isNaN
// test — the guard attachPreviousScores and rowSort each write out for themselves.
const instantOf = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return isNaN(time) ? null : time;
};

// A row's series identity. Keyed by String so a response spelling one company's id as a number and
// another's as a string still groups each with itself — the normalization both existing groupers
// apply. Null for a row with no company at all: a macro row has none, and pooling the id-less rows
// would invent a company out of unrelated ones.
export const seriesKey = (row) => {
  if (!row || row.companyId === null || row.companyId === undefined) return null;
  return String(row.companyId);
};

// One company's occasions, oldest first.
//
// TWO ROWS AT ONE INSTANT ARE PEERS, NOT A SEQUENCE. The endpoint claims (company_id, knowable_at)
// is unique and nothing enforces it — normalizeRow appends the array index to `row.key` for exactly
// that reason — so the plotted point for an instant is the LAST row in server order. Arbitrary,
// deliberately deterministic, and the same tie-break attachPreviousScores takes when it picks a
// predecessor, so the line and the Prev score column cannot disagree about which row an instant
// meant.
const occasionsOf = (entries) => {
  const byInstant = new Map();
  entries.forEach(({ row, instant, index }) => {
    const held = byInstant.get(instant);
    if (!held || index >= held.index) byInstant.set(instant, { row, instant, index });
  });
  return Array.from(byInstant.values()).sort((a, b) => a.instant - b.instant);
};

const summarize = (companyId, occasions) => {
  const rows = occasions.map((occasion) => occasion.row);
  const instants = occasions.map((occasion) => occasion.instant);
  const scored = rows.filter((row) => asFiniteNumber(row.score) !== null);
  const first = scored.length ? scored[0] : null;
  const last = scored.length ? scored[scored.length - 1] : null;
  return {
    companyId,
    // Every occasion of this company in the read, unscored ones included — they are what the breaks
    // are drawn from, so dropping them here would erase the gaps two files down.
    rows,
    instants,
    scored,
    first,
    last,
    scoredCount: scored.length,
    gapCount: rows.length - scored.length,
    // First to last SCORED, which is a different question from first to last ROW. Null under two
    // scored occasions: there is no measured movement, and 0 would claim the company didn't move.
    net: scored.length >= MIN_PLOTTABLE ? last.score - first.score : null,
    from: rows.length ? rows[0].knowableAt : null,
    to: rows.length ? rows[rows.length - 1].knowableAt : null,
  };
};

// company -> its series. Rows with no company, or with no parseable stamp, take no part: without
// either there is nothing to place a point on, or nobody to place it for.
export const buildSeriesIndex = (rows) => {
  const grouped = new Map();

  (rows || []).forEach((row, index) => {
    const key = seriesKey(row);
    if (key === null) return;
    const instant = instantOf(row?.knowableAt);
    if (instant === null) return;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push({ row, instant, index });
  });

  const index = new Map();
  grouped.forEach((entries, key) => index.set(key, summarize(key, occasionsOf(entries))));
  return index;
};

// The scales every cell is judged on.
//
// Read-wide, and deliberately not per row: per-row autoscaling gives a company that moved 0.08 the
// same silhouette as one that moved 2.48, which is a lie about magnitude told in the one channel a
// sparkline has. Same argument scoreScale and inputValueScale already make for the washes —
// saturation is judged against the whole response, so filtering doesn't restate what a shade means
// halfway through reading the table.
//
// `padFraction` is for the history panel, which plots one company at chart size and wants air above
// and below the line. The cell passes none: its box is 26px and every pixel of it is signal.
export const seriesDomains = (rows, { padFraction = 0 } = {}) => {
  const instants = (rows || []).map((row) => instantOf(row?.knowableAt)).filter((value) => value !== null);
  const scores = (rows || []).map((row) => asFiniteNumber(row?.score)).filter((value) => value !== null);

  const t0 = instants.length ? Math.min(...instants) : 0;
  const t1 = instants.length ? Math.max(...instants) : 0;

  let y0 = scores.length ? Math.min(...scores) : 0;
  let y1 = scores.length ? Math.max(...scores) : 1;
  // A flat series has no height to divide by, and every point would land on one edge of the box.
  // Half a unit either way centres it instead, which is the honest picture of a score that didn't
  // move: a line through the middle.
  if (y0 === y1) {
    y0 -= 0.5;
    y1 += 0.5;
  } else if (padFraction) {
    const pad = (y1 - y0) * padFraction;
    // PADDING MAY APPROACH ZERO AND MUST NOT CROSS IT. Air above and below stops a line reading as
    // clipped, but widening the domain past zero invents axis the data never reached: a series whose
    // lowest score is 0.05 padded to -0.03 gains a zero line and a strip of negative scale, and a
    // reader checking whether this company ever went negative gets the wrong answer off the frame.
    // A series that genuinely straddles zero is padded both ways as before.
    y0 = y0 >= 0 ? Math.max(0, y0 - pad) : y0 - pad;
    y1 = y1 <= 0 ? Math.min(0, y1 + pad) : y1 + pad;
  }

  return { t0, t1, y0, y1 };
};

// Consecutive scored occasions, split at every unscored one, plus the instants that split them.
// This is the single place the gap rule is implemented: nothing is drawn across a gap, and nothing
// is ever plotted at zero to stand in for one.
export const segmentsOf = (series) => {
  const segments = [];
  const gaps = [];
  let current = [];

  (series?.rows || []).forEach((row) => {
    if (asFiniteNumber(row.score) === null) {
      if (current.length) segments.push(current);
      current = [];
      gaps.push(row);
      return;
    }
    current.push(row);
  });
  if (current.length) segments.push(current);

  return { segments, gaps };
};

// Series + domains + a box -> everything a renderer needs, in that box's coordinates.
//
// The scale functions come back with it so the panel can place a zero line and a crosshair without
// re-deriving the arithmetic this module just did.
export const plotSeries = (series, domains, box = SPARK_BOX) => {
  const { width, height } = box;
  // A number is the same inset on all four sides — what the 26px cell wants. The history chart
  // passes an object, because its left and bottom have to hold axis labels the cell has no room for.
  const pad =
    typeof box.padding === 'number'
      ? { top: box.padding, right: box.padding, bottom: box.padding, left: box.padding }
      : { top: 0, right: 0, bottom: 0, left: 0, ...(box.padding || {}) };
  const spanT = domains.t1 - domains.t0;
  const spanY = domains.y1 - domains.y0;
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  // A read covering one instant has no width to spread over; centring is the only placement that
  // isn't a claim about where in the span it sat.
  const x = (instant) => (spanT > 0 ? pad.left + ((instant - domains.t0) / spanT) * innerW : pad.left + innerW / 2);
  const y = (score) =>
    spanY > 0 ? height - pad.bottom - ((score - domains.y0) / spanY) * innerH : pad.top + innerH / 2;

  const { segments, gaps } = segmentsOf(series);
  const at = (row) => ({ x: x(instantOf(row.knowableAt)), y: y(row.score), row });

  const paths = [];
  const isolated = [];
  segments.forEach((segment) => {
    const points = segment.map(at);
    if (points.length === 1) {
      // A scored occasion with a gap on both sides. It is a real number and it gets a mark rather
      // than being dropped for want of a neighbour to join.
      isolated.push(points[0]);
      return;
    }
    paths.push(points.map((point, i) => `${i === 0 ? 'M' : 'L'}${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' '));
  });

  const points = (series?.scored || []).map(at);
  const gapPoints = gaps.map((row) => ({ x: x(instantOf(row.knowableAt)), row }));

  return {
    x,
    y,
    // The drawing area, so a caller placing axes or a crosshair doesn't re-derive the insets.
    plotBox: { top: pad.top, right: width - pad.right, bottom: height - pad.bottom, left: pad.left },
    paths,
    isolated,
    gaps: gapPoints,
    points,
    last: points.length ? points[points.length - 1] : null,
    // The same two figures as one path each, for the table cell.
    //
    // Node count is the reason. A page is 1,000 rows and a capped response can reach 25,000, so an
    // element per segment and an element per gap is a five-figure DOM on a wide feed. One subpath per
    // segment says exactly what several elements said — a break between subpaths is still a break —
    // at one node. The chart, which draws a single company, keeps the elements.
    // Isolated points ride in the same path as zero-length subpaths, which a round line cap draws as
    // a dot. On a multi-input feed — whose rows are the union grid of its members' occasions, so a
    // scored occasion between two gaps is the NORM — that is twenty fewer elements a row.
    pathD: paths
      .concat(
        isolated.map(
          (point) => `M${point.x.toFixed(2)} ${point.y.toFixed(2)} L${point.x.toFixed(2)} ${point.y.toFixed(2)}`
        )
      )
      .join(' '),
    gapD: gapPoints
      .map((gap) => `M${gap.x.toFixed(2)} ${pad.top - 1} L${gap.x.toFixed(2)} ${(height - pad.bottom + 1).toFixed(2)}`)
      .join(' '),
  };
};

// What the cell is called, and what its tooltip says. One writer, so the accessible name and the
// pointer tooltip can never describe the same line differently — the rule querySummary already
// applies to the query.
export const describeSeries = (series, { currentRow = null } = {}) => {
  if (!series || !series.rows.length) return 'No occasion for this company in this read.';

  const { scored, scoredCount, gapCount, net } = series;

  if (scoredCount === 0) {
    return `No scored occasion in this read — ${formatCount(series.rows.length)} occasion${
      series.rows.length === 1 ? '' : 's'
    }, none of them scored.`;
  }

  if (scoredCount < MIN_PLOTTABLE) {
    return `One scored occasion: ${formatScore(scored[0].score)} on ${formatUtcDate(
      scored[0].knowableAt
    )}. Two are the floor for a line.`;
  }

  const first = series.first;
  const last = series.last;
  const gapNote = gapCount
    ? ` ${formatCount(gapCount)} occasion${gapCount === 1 ? '' : 's'} with no score, drawn as a break.`
    : '';
  const shape = `${formatScore(first.score)} → ${formatScore(last.score)} (${formatScoreDelta(
    net
  )}) across ${formatCount(scoredCount)} scored occasions, ${formatUtcDate(first.knowableAt)} → ${formatUtcDate(
    last.knowableAt
  )}.${gapNote}`;

  if (!currentRow) return shape;

  // The per-occasion grain. The line is the company's; the mark is this row's, and the sentence has
  // to say which is which or the cell reads as a claim about the row alone.
  // By instant rather than by object identity: the rows reaching a cell have been through a filter,
  // an aggregate and a sort, and nothing in that pipeline promises the same object back.
  const position = series.instants.indexOf(instantOf(currentRow.knowableAt));
  const place = position >= 0 ? ` — ${position + 1} of ${formatCount(series.rows.length)} in this read` : '';
  const here =
    asFiniteNumber(currentRow.score) === null
      ? `This occasion has no score, ${formatUtcDate(currentRow.knowableAt)}`
      : `This occasion: ${formatScore(currentRow.score)} on ${formatUtcDate(currentRow.knowableAt)}`;

  return `${here}${place}. Series ${shape}`;
};

// What the table builds once per read: the series, the domains they share, and the cell geometry —
// which is per COMPANY rather than per row, so a company's line is computed once however many rows
// it holds. Everything here is keyed on the response, so filtering and sorting never rebuild it.
export const buildMovementIndex = (rows) => {
  const domains = seriesDomains(rows);
  const seriesIndex = buildSeriesIndex(rows);
  const byCompany = new Map();
  seriesIndex.forEach((series, key) => byCompany.set(key, { series, plot: plotSeries(series, domains, SPARK_BOX) }));
  return { domains, byCompany };
};

export const movementEntry = (index, row) => {
  const key = seriesKey(row);
  if (key === null) return null;
  return index?.byCompany?.get(key) || null;
};

// The net change, put on every row so the sort has a number to order by. Attached once per read
// rather than per keystroke, and BEFORE the table aggregates: aggregateByCompany spreads the
// company's latest row, so an aggregated row inherits the company's net without a second pass.
//
// Null — never 0 — for a company with fewer than two scored occasions. isEmpty in rowSort puts a
// null in the absent group, which is where a company with no measured movement belongs; a 0 would
// sort it among the companies that genuinely didn't move.
export const attachMovement = (rows, index) =>
  (rows || []).map((row) => {
    const entry = movementEntry(index, row);
    return { ...row, movementNet: entry ? entry.series.net : null };
  });
